import { useLocalizedPath, useSiteLocale } from "../i18n/useLocalizedPath";
import { localizeHtmlLinks } from "../i18n/localizedRoutes";
import React from "react";
import {
  Box,
  VStack,
  Image,
  Text,
  Button,
  Grid,
  GridItem,
  Heading,
  Container,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { localizedNewsCover, newsData } from "../data/newsData";
import NewsSidebar from "./NewsSidebar";
import { imageMap } from "../utils/imageMap";
import { getAssetLanguageKey, normalizeSiteLanguage } from "../i18n/language";
import { getNewsCopy } from "../i18n/content";
import SEO from "./SEO";

const News: React.FC = () => {
  const { t, i18n } = useTranslation();
  const localizePath = useLocalizedPath();
  const siteLocale = useSiteLocale();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;
  const assetLang = getAssetLanguageKey(activeLanguage);
  const locale = normalizeSiteLanguage(activeLanguage);

  const getNewsImage = (item: (typeof newsData)[number]) => {
    const languageImage =
      localizedNewsCover(item, activeLanguage) ||
      item.thumbnailByLang?.[assetLang] ||
      item.imagesByLang?.[assetLang]?.[0];
    const image =
      languageImage || item.coverImage || item.thumbnail || item.images?.[0];

    return image ? imageMap[image] || image : "";
  };


  const getDateDisplay = (date: string) => {
    try {
      const dateObj = new Date(date);
      if (dateObj.toString() === "Invalid Date") {
        return date;
      }
      return dateObj.toLocaleDateString(
        locale === "en" ? "en-US" : locale === "zh-CN" ? "zh-CN" : "zh-HK",
      );
    } catch (error) {
      return date;
    }
  };

  return (
    <>
      <SEO
        title={t("seo.pages.news.title")}
        description={t("seo.pages.news.description")}
        keywords={t("seo.pages.news.keywords")}
        canonical="https://deepsoulai.com/news"
      />
      <Container
        maxW="1400px"
        px={{ base: 4, md: 6 }}
        py={{ base: 5, md: 8 }}
      >
        <Heading as="h1" size="xl" mb={8} textAlign="center">
          {t("news.title")}
        </Heading>

        <Grid
          templateColumns={{ base: "1fr", lg: "2fr 1fr" }}
          gap={{ base: 6, lg: 8 }}
        >
          {/* Main Content */}
          <GridItem>
            <VStack spacing={{ base: 5, md: 6 }} align="stretch">
              {newsData.map((item) => (
                <Box
                  key={item.id}
                  bg="white"
                  borderRadius={{ base: "xl", md: "lg" }}
                  boxShadow={{ base: "sm", md: "md" }}
                  _hover={{ boxShadow: "lg", transform: "translateY(-2px)" }}
                  transition="all 0.3s ease"
                  overflow="hidden"
                  p={{ base: 0, md: 6 }}
                >
                  {getNewsImage(item) && (
                    <Image
                      src={getNewsImage(item)}
                      // src={imageMap[item.images[0]] || item.images[0]}
                      alt={getNewsCopy(t, item.id, "title")}
                      w={{ base: "auto", md: "100%" }}
                      maxW="100%"
                      h={{ base: "auto", md: "500px" }}
                      maxH={{ base: "420px", sm: "480px", md: "500px" }}
                      objectFit="contain"
                      mx="auto"
                      fallbackSrc="https://via.placeholder.com/800x250?text=News+Banner"
                      borderRadius={{ base: "xl xl 0 0", md: "lg" }}
                      bg="gray.50"
                      mb={{ base: 0, md: 3 }}
                    />
                  )}
                  <VStack
                    align="start"
                    spacing={{ base: 2, md: 3 }}
                    p={{ base: 4, sm: 5, md: 0 }}
                  >
                    <Box>
                      <Heading as="h2" size="md" lineHeight="1.3" mb={1}>
                        {getNewsCopy(t, item.id, "title")}
                      </Heading>
                      <Text fontSize="sm" color="gray.500">
                        {getNewsCopy(t, item.id, "dateDisplay") ||
                          getDateDisplay(item.date)}
                      </Text>
                    </Box>
                    <Text
                      color="gray.600"
                      noOfLines={{ base: 2, md: 3 }}
                      dangerouslySetInnerHTML={{
                        __html: localizeHtmlLinks(getNewsCopy(t, item.id, "content"), siteLocale),
                      }}
                      sx={{
                        "& a": {
                          color: "blue.500",
                          textDecoration: "underline",
                          _hover: {
                            color: "blue.700",
                            textDecoration: "none",
                          },
                        },
                      }}
                    />
                    <Button
                      colorScheme="primary"
                      size="sm"
                      px={5}
                      py={2}
                      mt={{ base: 1, md: 0 }}
                      fontWeight={500}
                      as={RouterLink}
                      to={localizePath(`/news/${item.id}`)}
                      aria-label={getNewsCopy(t, item.id, "title")}
                    >
                      {t("news.readMore")}
                    </Button>
                  </VStack>
                </Box>
              ))}
            </VStack>
          </GridItem>

          {/* Sidebar */}
          <GridItem display={{ base: "none", lg: "block" }}>
            <NewsSidebar />
          </GridItem>
        </Grid>
      </Container>
    </>
  );
};

export default News;

